import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@common/components';
import * as styles from './ReportDetailsModal.module.css';

/**
 * ReportDetailsModal Component
 * Modal for editing report name and description
 * Displays inline error messages and auto-clears errors when user types
 */
function ReportDetailsModal({
  isOpen,
  initialName = '',
  initialDescription = '',
  onSave,
  onCancel,
  saving = false,
  error = null,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const nameInputRef = useRef(null);

  // Derived error state: show error prop unless user dismissed it by typing
  const visibleError = error && !errorDismissed ? error : null;

  // Reset errorDismissed when error prop changes (new error from parent)
  useEffect(() => {
    setErrorDismissed(false);
    if (error) {
      nameInputRef.current?.focus();
    }
  }, [error]);

  // Reset form when modal opens with new defaults
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
      setErrorDismissed(false);
    }
  }, [isOpen, initialName, initialDescription]);

  // Clear error when user types in name field
  const handleNameChange = useCallback((e) => {
    setName(e.target.value);
    setErrorDismissed(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim(),
    });
  }, [name, description, onSave]);

  const handleCancel = useCallback(() => {
    setName(initialName);
    setDescription(initialDescription);
    onCancel();
  }, [initialName, initialDescription, onCancel]);

  const modalBody = (
    <div className={styles.modalContent}>
      <div className={styles.field}>
        <label htmlFor="reportName" className={styles.label}>
          {t('reports.reportName')}
          <span className={styles.required}>*</span>
        </label>
        <input
          ref={nameInputRef}
          id="reportName"
          type="text"
          className={`${styles.input} ${visibleError ? styles.inputError : ''}`}
          value={name}
          onChange={handleNameChange}
          placeholder={t('reports.enterReportName')}
          disabled={saving}
          aria-invalid={!!visibleError}
          aria-describedby={visibleError ? 'report-name-error' : undefined}
        />
        {visibleError && (
          <div
            id="report-name-error"
            className={styles.fieldError}
            role="alert"
          >
            {visibleError}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor="reportDescription" className={styles.label}>
          {t('common.description')}
        </label>
        <textarea
          id="reportDescription"
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('reports.enterDescription')}
          disabled={saving}
          rows={4}
        />
      </div>

      <div className={styles.footer}>
        <Button variant="outline" onClick={handleCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!name.trim() || saving}
        >
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('reports.editReportDetails')}
      body={modalBody}
      hideFooter
      maxWidth="500px"
    />
  );
}

ReportDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialName: PropTypes.string,
  initialDescription: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
};

ReportDetailsModal.defaultProps = {
  initialName: '',
  initialDescription: '',
  saving: false,
  error: null,
};

export default ReportDetailsModal;

